import { memo, useState, useEffect, useCallback } from 'react'
import { mediaBrowserApi, type MediaFile, type ProbeResult } from '../../lib/mediaBrowserApi.js'
import { MediaFileRow } from './MediaFileRow.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

// ─── Props ────────────────────────────────────────────────────────────────────

interface MediaBrowserModalProps {
  onSelect: (result: ProbeResult) => void
  onClose:  () => void
  /** Inclut aussi les fichiers image (choix d'une miniature de série par ex.). */
  includeImages?: boolean
  /** Libellé du bouton de validation d'un fichier. « Associer » par défaut. */
  confirmLabel?: string
  /** Ne montre que des morceaux audio (musique de l'infoservice), sans vidéo. */
  audioOnly?: boolean
  /** Dossier ouvert d'emblée, relatif à la racine média ; on peut remonter. */
  initialPath?: string
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const MediaBrowserModal = memo(function MediaBrowserModal({ onSelect, onClose, includeImages, confirmLabel = 'Associer', audioOnly, initialPath }: MediaBrowserModalProps) {
  const [files,       setFiles]       = useState<MediaFile[]>([])
  const [basePath,    setBasePath]    = useState('')
  const [currentPath, setCurrentPath] = useState('') // relatif au basePath
  const [isSearchMode, setIsSearchMode] = useState(false)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [search,      setSearch]      = useState('')
  const [selected,    setSelected]    = useState<MediaFile | null>(null)
  const [probing,     setProbing]     = useState(false)
  const [probeErr,    setProbeErr]    = useState('')

  // Chargement initial et navigation
  const loadFolder = useCallback((path: string, query?: string) => {
    setLoading(true)
    setError('')
    mediaBrowserApi.list(path, query, includeImages, audioOnly)
      .then(r => {
        setFiles(r.files)
        setBasePath(r.path)
        setCurrentPath(r.subPath)
        setIsSearchMode(r.isSearch)
        setSelected(null)
      })
      .catch(e => setError(e.message ?? 'Erreur de chargement'))
      .finally(() => setLoading(false))
  }, [audioOnly, includeImages])

  // Effet pour la recherche debouncée
  useEffect(() => {
    if (!search.trim()) {
      // Si on vide la recherche, on revient au dossier courant (ou racine)
      if (isSearchMode) loadFolder(currentPath)
      return
    }

    const timer = setTimeout(() => {
      loadFolder('', search)
    }, 350)

    return () => clearTimeout(timer)
  }, [search, loadFolder])

  // Chargement initial
  useEffect(() => {
    loadFolder(initialPath ?? '')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = useCallback(async () => {
    if (!selected) return
    if (selected.type === 'directory') {
      setSearch('') // On quitte la recherche si on entre dans un dossier
      loadFolder(selected.relativePath)
      return
    }
    
    setProbing(true)
    setProbeErr('')
    try {
      const result = await mediaBrowserApi.probe(selected.path)
      onSelect(result)
    } catch (e: any) {
      setProbeErr(e.message ?? 'Erreur d\'analyse du fichier')
    } finally {
      setProbing(false)
    }
  }, [selected, onSelect, loadFolder])

  const goBack = useCallback(() => {
    if (isSearchMode) {
      setSearch('')
      loadFolder(currentPath)
      return
    }
    if (!currentPath) return
    const parts = currentPath.split('/')
    parts.pop()
    loadFolder(parts.join('/'))
  }, [currentPath, isSearchMode, loadFolder])

  return (
    <ModalShell
      className="catalog-media-browser"
      onClose={onClose}
      size="wide"
      title={isSearchMode ? 'Résultats de recherche' : 'Navigateur de médias'}
    >
        <div className="catalog-media-browser__path">
          {basePath}{!isSearchMode && currentPath ? ` / ${currentPath}` : ''}
        </div>

        {/* Barre de recherche et navigation */}
        <div className="catalog-media-browser__toolbar">
          {(currentPath || isSearchMode) && (
            <button
              onClick={goBack}
              className="tp-icon-button catalog-media-browser__back"
              aria-label={isSearchMode ? "Quitter la recherche" : "Retour au dossier parent"}
              title={isSearchMode ? "Quitter la recherche" : "Retour au dossier parent"}
            >
              {isSearchMode ? '✕' : '↑'}
            </button>
          )}
          <input
            type="text"
            placeholder="Rechercher dans tous les dossiers…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            className="tp-input"
          />
        </div>

        {/* Liste des fichiers */}
        <div className="catalog-media-browser__list">
          {loading && (
            <div className="tp-state catalog-media-browser__state" aria-live="polite">
              <Spinner /> Chargement…
            </div>
          )}
          {error && (
            <div className="tp-state tp-feedback--error catalog-media-browser__state" role="alert">
              ⚠ {error}<br/>
              <span className="catalog-media-browser__state-detail">
                Vérifiez le chemin du dossier média dans les Réglages.
              </span>
            </div>
          )}
          {!loading && !error && files.length === 0 && (
            <div className="tp-state catalog-media-browser__state is-empty">
              {search ? 'Aucun élément ne correspond à la recherche' : 'Dossier vide'}
            </div>
          )}
          {!loading && !error && files.map(file => (
            <MediaFileRow
              key={file.path}
              file={file}
              showPath={isSearchMode}
              selected={selected?.path === file.path}
              onClick={() => { setSelected(file); setProbeErr('') }}
              onDoubleClick={() => {
                if (file.type === 'directory') {
                  setSearch('')
                  loadFolder(file.relativePath)
                } else {
                  handleConfirmWithItem(file)
                }
              }}
            />
          ))}
        </div>

        {/* Pied — sélection + confirmation */}
        <div className="catalog-media-browser__footer">
          <div className="catalog-media-browser__selection">
            {selected ? (
              <div className="catalog-media-browser__selection-name">
                <span className="catalog-media-browser__selection-type">{selected.type === 'directory' ? 'Dossier' : 'Fichier'} : </span>
                {selected.name}
              </div>
            ) : (
              <div className="catalog-media-browser__selection-empty">
                {files.length} élément{files.length !== 1 ? 's' : ''}
              </div>
            )}
            {probeErr && (
              <div className="catalog-media-browser__probe-error" role="alert">
                ⚠ {probeErr}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="tp-button"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selected || probing}
            className={`tp-button ${selected?.type === 'directory' ? 'tp-button--info' : 'tp-button--success-outline'}`}
            data-state={probing ? 'loading' : undefined}
          >
            {probing ? 'Analyse…' : selected?.type === 'directory' ? 'Ouvrir' : confirmLabel}
          </button>
        </div>
    </ModalShell>
  )

  async function handleConfirmWithItem(item: MediaFile) {
    setProbing(true)
    setProbeErr('')
    try {
      const result = await mediaBrowserApi.probe(item.path)
      onSelect(result)
    } catch (e: any) {
      setProbeErr(e.message ?? 'Erreur d\'analyse du fichier')
    } finally {
      setProbing(false)
    }
  }
})

